/**
 * CEO Command Center - Zustand Stores
 *
 * Global state management using Zustand.
 * Provides reactive state for:
 * - Application data (tasks, deals, clients, etc.) via SQLite
 * - UI state (current view, modals, toasts)
 * - User preferences
 *
 * NOTE: v11.0.0 - Airtable has been deprecated. All data is now stored locally
 * in SQLite via CR-SQLite. The useDataStore export is an alias to useDataStoreSQLite
 * for backward compatibility.
 *
 * @module stores
 * @version 11.0.0
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Task,
  Deal,
  ViewId,
  User,
  UserConfig,
  Toast,
} from '../types';
import { authStorage, configStorage } from '../services/storage';
import { DEFAULT_CONFIG } from '../config/constants';

// v11: SQLite is the only backend - useDataStore is an alias for backward compatibility
export { useDataStoreSQLite as useDataStore } from './dataStoreSQLite';
export {
  useTasksByDomainSQLite as useTasksByDomain,
  useActiveDealsSQLite as useActiveDeals,
  useSignalTasksSQLite as useSignalTasks,
  useOverdueTasksSQLite as useOverdueTasks,
  useTodayTasksSQLite as useTodayTasks,
  useSignalCountsSQLite as useSignalCounts,
  useBoardReadinessSQLite as useBoardReadiness,
} from './dataStoreSQLite';

// Also export the SQLite store with its original name for explicit usage
export { useDataStoreSQLite } from './dataStoreSQLite';

// Re-export sync store
export { useSyncStore } from './syncStore';

// Re-export knowledge store
export { useKnowledgeStore, useKnowledgeCategories, useKnowledgeSearch } from './knowledgeStore';
export type { KnowledgeItem, KnowledgeCategory } from './knowledgeStore';

// Re-export document store
export { useDocumentStore, useDomainDocuments, useInitiativeDocuments } from './documentStore';

// Re-export domain store
export { useDomainStore } from './domainStore';

// Re-export initiative store
export {
  useInitiativeStore,
  useInitiativesWithMetrics,
  useActiveInitiatives,
  useSelectedInitiative,
  useInitiativeTaskCounts,
} from './initiativeStore';

// Re-export connection store
export {
  useConnectionStore,
  useConnectionHealth,
  useConnectionError,
} from './connectionStore';

// Re-export storage mode (always SQLite now)
export { useStorageMode, getStorageMode } from './dataStoreProvider';

// UI STORE

interface UIState {
  // Current view
  currentView: ViewId;

  // Modals
  taskEditorOpen: boolean;
  taskEditorTask: Task | null;
  dealEditorOpen: boolean;
  dealEditorDeal: Deal | null;
  settingsOpen: boolean;

  // Chat
  chatOpen: boolean;

  // Toasts
  toasts: Toast[];

  // Actions
  setView: (view: ViewId) => void;
  openTaskEditor: (task?: Task | null) => void;
  closeTaskEditor: () => void;
  openDealEditor: (deal?: Deal | null) => void;
  closeDealEditor: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  toggleChat: () => void;
  setChat: (open: boolean) => void;
  showToast: (message: string, type?: Toast['type']) => void;
  dismissToast: (id: string) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentView: 'command',
      taskEditorOpen: false,
      taskEditorTask: null,
      dealEditorOpen: false,
      dealEditorDeal: null,
      settingsOpen: false,
      chatOpen: false,
      toasts: [],

      // Set current view
      setView: (view) => set({ currentView: view }),

      // Task editor
      openTaskEditor: (task = null) => set({
        taskEditorOpen: true,
        taskEditorTask: task,
      }),
      closeTaskEditor: () => set({
        taskEditorOpen: false,
        taskEditorTask: null,
      }),

      // Deal editor
      openDealEditor: (deal = null) => set({
        dealEditorOpen: true,
        dealEditorDeal: deal,
      }),
      closeDealEditor: () => set({
        dealEditorOpen: false,
        dealEditorDeal: null,
      }),

      // Settings
      openSettings: () => set({ settingsOpen: true }),
      closeSettings: () => set({ settingsOpen: false }),

      // Chat
      toggleChat: () => set(state => ({ chatOpen: !state.chatOpen })),
      setChat: (open) => set({ chatOpen: open }),

      // Toasts
      showToast: (message, type = 'info') => {
        const id = Date.now().toString();
        set(state => ({
          toasts: [...state.toasts, { id, message, type }],
        }));

        // Auto dismiss after 3.5 seconds
        setTimeout(() => {
          get().dismissToast(id);
        }, 3500);
      },

      dismissToast: (id) => set(state => ({
        toasts: state.toasts.filter(t => t.id !== id),
      })),
    }),
    { name: 'ceo-ui-store' }
  )
);

// AUTH STORE

interface AuthState {
  user: User | null;
  config: UserConfig;

  // Actions
  login: (name: string) => void;
  logout: () => void;
  updateConfig: (updates: Partial<UserConfig>) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state from localStorage
        user: authStorage.getUser(),
        config: configStorage.getConfig() || DEFAULT_CONFIG,

        // Login
        login: (name) => {
          const user: User = {
            name,
            loginAt: new Date().toISOString(),
          };
          authStorage.setUser(user);
          set({ user });
        },

        // Logout
        logout: () => {
          authStorage.clearUser();
          set({ user: null });
        },

        // Update config
        updateConfig: (updates) => {
          const newConfig = { ...get().config, ...updates };
          configStorage.setConfig(newConfig);
          set({ config: newConfig });
        },
      }),
      {
        name: 'ceo-auth-store',
        partialize: (state) => ({ config: state.config }),
      }
    ),
    { name: 'ceo-auth-store' }
  )
);
